/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  Store,
  Truck,
  Calendar,
  FileText,
  Home,
  User,
  Plus,
  IndianRupee,
  Trash2,
  ChevronsUpDown,
  Wallet2,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  API_URL,
  ATTRIBUTE_KEYS,
  DELIVERY_PREPARATION_TIMES,
  DELIVERY_SLOT_INTERVALS,
  SEVEN_DAYS_OPEN_VALUE,
  USER_ROLES,
} from '../../../constant/constant';
import { TimeSelector } from '../../../components/common/TimeSelector';
import { DeliveryTypeSelector } from '../../../components/common/DeliveryTypeSelector';
import { useUser } from '../../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import BiglocalLoader from '../../../components/common/BigLocalLoader';
import { getAttributeLabel } from '../../../utils/VendorAttributesUtils';
import axiosInstance from '../../../utils/axiosInstance';

const WEEKLY_OFF_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select...',
  className,
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (option) => {
    let newSelectedValues;
    if (option === SEVEN_DAYS_OPEN_VALUE) {
      newSelectedValues = [SEVEN_DAYS_OPEN_VALUE];
    } else {
      const currentDays = selectedValues.filter((v) => v !== SEVEN_DAYS_OPEN_VALUE);
      const isSelected = currentDays.includes(option);
      newSelectedValues = isSelected
        ? currentDays.filter((v) => v !== option)
        : [...currentDays, option];
    }
    onChange(newSelectedValues);
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.includes(SEVEN_DAYS_OPEN_VALUE)) {
      return SEVEN_DAYS_OPEN_VALUE;
    }
    return selectedValues.map((val) => val.charAt(0) + val.slice(1).toLowerCase()).join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal ${className}`}
        >
          <span className="truncate">{getButtonLabel()}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {/* Option to clear the selection */}
              <CommandItem
                value={'clear'}
                onSelect={() => {
                  onChange([]); // Set to empty array to clear
                  setOpen(false);
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <div className="h-4 w-4" /> {/* Spacer */}
                <span className="text-slate-600 italic">-- Not Specified --</span>
              </CommandItem>

              {/* 7 Days Open Option */}
              <CommandItem
                value={SEVEN_DAYS_OPEN_VALUE}
                onSelect={() => {
                  handleSelect(SEVEN_DAYS_OPEN_VALUE);
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(SEVEN_DAYS_OPEN_VALUE)}
                  readOnly
                  className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 pointer-events-none"
                />
                <span>{SEVEN_DAYS_OPEN_VALUE}</span>
              </CommandItem>

              {/* Individual Day Options */}
              {options.map((option) => {
                const isSelected =
                  selectedValues.includes(option) &&
                  !selectedValues.includes(SEVEN_DAYS_OPEN_VALUE);
                return (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => {
                      handleSelect(option);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 pointer-events-none"
                    />
                    <span>{option.charAt(0) + option.slice(1).toLowerCase()}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DELIVERY_TYPE_OPTIONS = {
  product: [
    {
      value: 'Self Pickup',
      label: 'Self Pickup',
      icon: Store,
      description: 'Customers pick up from your location.',
    },
    {
      value: 'Home Delivery',
      label: 'Home Delivery',
      icon: Truck,
      description: 'You deliver directly to their address.',
    },
    {
      value: 'Both',
      label: 'Both',
      icon: CheckCircle,
      description: 'Offer both pickup and delivery options.',
    },
  ],
  service: [
    {
      value: 'Self Visit',
      label: 'At Your Location',
      icon: User,
      description: 'Customers visit your premises',
    },
    {
      value: 'Home Service',
      label: "At Customer's Location",
      icon: Home,
      description: 'Your team visit customer location',
    },
    {
      value: 'Both',
      label: 'Both',
      icon: CheckCircle,
      description: 'Provide services at both locations',
    },
  ],
};

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const UDYAM_REGEX = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;

const InputWithIcon = ({ id, label, helperText, value, onChange, icon: Icon, error, ...props }) => (
  <div>
    <Label htmlFor={id} className="font-semibold text-slate-800">
      {label}
    </Label>
    <div className="relative mt-1">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>
      <Input
        id={id}
        value={value}
        onChange={onChange}
        className={`pl-10 h-11 focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1 ${error ? 'border-red-500' : ''}`}
        {...props}
      />
    </div>
    {helperText && !error && <p className="mt-1.5 text-xs text-slate-500">{helperText}</p>}
    {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
  </div>
);

// --- MAIN FORM COMPONENT (Refactored to accept props) ---
const VendorShopSettings = () => {
  const [vendor, setVendor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [noOffHours, setNoOffHours] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedDays, setSelectedDays] = useState([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    attributes: [],
    serviceSlotInterval: '1',
    productSlotInterval: '1',
  });
  const [certificates, setCertificates] = useState([]);
  const { user, refreshUser } = useUser();
  const [franchises, setFranchises] = useState([]);
  const [useShopTimings, setUseShopTimings] = useState({ service: true });
  const [providesServices, setProvidesServices] = useState(false);
  const [customSlotTimings, setCustomSlotTimings] = useState({
    service: { start: '', end: '' },
    subscription: { start: '', end: '' },
  });
  const [isEventVendor, setIsEventVendor] = useState(false);

  const getAttribute = useCallback(
    (name) => formData.attributes.find((attr) => attr.name === name),
    [formData.attributes]
  );
  const getAttributeValue = useCallback((name) => getAttribute(name)?.value ?? '', [getAttribute]);

  const setAttributeValue = useCallback((name, value) => {
    setFormData((prev) => {
      const newAttributes = [...prev.attributes];
      const attrIndex = newAttributes.findIndex((attr) => attr.name === name);
      if (attrIndex > -1) newAttributes[attrIndex] = { ...newAttributes[attrIndex], value };
      else newAttributes.push({ name, value, id: null });
      return { ...prev, attributes: newAttributes };
    });
  }, []);

  const handleAttributeChange = (name, value) => {
    setAttributeValue(name, value);
    const currYear = new Date().getFullYear();
    if (name === 'gstNo')
      setValidationErrors((prev) => ({
        ...prev,
        gstNo: value && !GST_REGEX.test(value) ? 'Invalid GST Number format.' : null,
      }));
    else if (name === 'panNo')
      setValidationErrors((prev) => ({
        ...prev,
        panNo: value && !PAN_REGEX.test(value) ? 'Invalid PAN Number format.' : null,
      }));
    else if (name === 'udyamNo')
      setValidationErrors((prev) => ({
        ...prev,
        udyamNo:
          value && !UDYAM_REGEX.test(value)
            ? 'Invalid Udyam format. (e.g., UDYAM-XX-00-0000000)'
            : null,
      }));
    else if (name === 'establishedYear')
      setValidationErrors((prev) => ({
        ...prev,
        establishedYear:
          value && value > currYear ? 'Established year cannot be in the future' : null,
      }));
  };

  const setNestedAttributeValue = useCallback(
    (attributeName, key, value) => {
      const currentAttr = getAttribute(attributeName);
      let parsedValue = {};
      try {
        parsedValue = currentAttr?.value ? JSON.parse(currentAttr.value) : {};
      } catch (e) {
        console.error(`Error parsing JSON for ${attributeName}:`, e);
      }
      const newValue = { ...parsedValue, [key]: value };
      setAttributeValue(attributeName, JSON.stringify(newValue));
    },
    [getAttribute, setAttributeValue]
  );

  const getNestedAttributeValue = useCallback(
    (attributeName, key) => {
      const attribute = getAttribute(attributeName);
      if (!attribute?.value) return '';
      try {
        return JSON.parse(attribute.value)[key] || '';
      } catch (e) {
        return '';
      }
    },
    [getAttribute]
  );

  const getDeliveryMethodValue = useCallback(
    () => JSON.parse(getAttributeValue('deliveryMethod') || '{}'),
    [getAttributeValue]
  );
  const setDeliveryMethodValue = useCallback(
    (type, method) =>
      setAttributeValue(
        'deliveryMethod',
        JSON.stringify({ ...getDeliveryMethodValue(), [type]: method })
      ),
    [getDeliveryMethodValue, setAttributeValue]
  );

  const getDeliveryPreparationTimeValue = useCallback(
    () => JSON.parse(getAttributeValue('deliveryPreparationTime') || '{}'),
    [getAttributeValue]
  );
  const setDeliveryPreparationTimeValue = useCallback(
    (type, time) =>
      setAttributeValue(
        'deliveryPreparationTime',
        JSON.stringify({ ...getDeliveryPreparationTimeValue(), [type]: time })
      ),
    [getDeliveryPreparationTimeValue, setAttributeValue]
  );

  const getDeliverySlotsValue = useCallback(
    () => JSON.parse(getAttributeValue('deliverySlots') || '{}'),
    [getAttributeValue]
  );
  const setDeliverySlotsValue = useCallback(
    (type, slots) =>
      setAttributeValue(
        'deliverySlots',
        JSON.stringify({ ...getDeliverySlotsValue(), [type]: slots })
      ),
    [getDeliverySlotsValue, setAttributeValue]
  );

  const parseSemicolonList = useCallback(
    (str) =>
      str
        ? str
            .split(';')
            .filter(Boolean)
            .map((name) => ({ name }))
        : [],
    []
  );
  const stringifySemicolonList = useCallback((arr) => arr.map((item) => item.name).join(';'), []);

  const parseCertificationsString = useCallback((str) => {
    if (!str) return [];
    const parts = str.split(';').filter(Boolean);
    const certs = [];
    for (let i = 0; i < parts.length; i += 2) {
      if (parts[i] && parts[i + 1]) certs.push({ name: parts[i], number: parts[i + 1] });
    }
    return certs;
  }, []);

  const stringifyCertificatesArray = useCallback(
    (certs) => certs.map((c) => `${c.name};${c.number}`).join(';'),
    []
  );

  useEffect(() => {
    const fetchAndMergeAttributes = async () => {
      if (!vendor) return;
      console.log(vendor);

      setIsLoading(true);
      try {
        const response = await axiosInstance.get(
          `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/attribute`,
          {
            headers: {
              sessionToken: localStorage.getItem('sessionToken'),
              'X-USER-ROLE': USER_ROLES.VENDOR,
            },
          }
        );
        const masterAttributes = response.data || [];
        const vendorAttributes = vendor?.attributeValues || [];

        console.log(masterAttributes);
        console.log(vendorAttributes);

        const mergedAttributes = masterAttributes.map((masterAttr) => {
          const vendorAttr = vendorAttributes.find((vAttr) => vAttr.name === masterAttr.name);
          let value = vendorAttr?.value ?? '';
          if (!vendorAttr) {
            // Set default empty structures for complex types
            if ([ATTRIBUTE_KEYS.SHOP_TIMING, ATTRIBUTE_KEYS.OFF_HOURS].includes(masterAttr.name))
              value = JSON.stringify({ start: '', end: '' });
            else if (
              [
                ATTRIBUTE_KEYS.DELIVERY_METHOD,
                ATTRIBUTE_KEYS.DELIVERY_PREPARATION_TIME,
                ATTRIBUTE_KEYS.DELIVERY_SLOTS,
              ].includes(masterAttr.name)
            )
              value = JSON.stringify({});
            else if (masterAttr.type === 'boolean') value = 'false';
          }
          return { ...masterAttr, value, id: vendorAttr?.id || masterAttr.id };
        });

        setFormData((prev) => ({ ...prev, attributes: mergedAttributes }));

        const findAttrVal = (name) => mergedAttributes.find((a) => a.name === name)?.value;

        const weeklyOffDayValue = findAttrVal(ATTRIBUTE_KEYS.WEEKLY_OFF_DAY);

        if (weeklyOffDayValue) {
          // Split the string into an array, filtering out any empty strings
          const daysArray = weeklyOffDayValue.split(';').filter(Boolean);
          setSelectedDays(daysArray);
        }

        setNoOffHours(
          !!findAttrVal(ATTRIBUTE_KEYS.OFF_HOURS) &&
            findAttrVal(ATTRIBUTE_KEYS.OFF_HOURS) !== JSON.stringify({ start: '', end: '' })
        );
        setCertificates(parseCertificationsString(findAttrVal(ATTRIBUTE_KEYS.CERTIFICATIONS)));
        setFranchises(parseSemicolonList(findAttrVal(ATTRIBUTE_KEYS.FRANCHISE_NAME)));

        const deliverySlotsValue = findAttrVal(ATTRIBUTE_KEYS.DELIVERY_SLOTS);
        const deliveryMethodValue = findAttrVal(ATTRIBUTE_KEYS.DELIVERY_METHOD);

        let parsedSlots = {};
        let parsedMethods = {};

        try {
          parsedSlots = deliverySlotsValue ? JSON.parse(deliverySlotsValue) : {};
        } catch (e) {
          console.error(e);
        }
        try {
          parsedMethods = deliveryMethodValue ? JSON.parse(deliveryMethodValue) : {};
        } catch (e) {
          console.error(e);
        }

        const hasServiceSlots =
          Array.isArray(parsedSlots.service) && parsedSlots.service.length > 0;
        const hasServiceMethod = !!parsedMethods.service;

        if (hasServiceSlots || hasServiceMethod) {
          setProvidesServices(true);
        }

        if (hasServiceSlots) {
          const shopTimings = findAttrVal(ATTRIBUTE_KEYS.SHOP_TIMING);
          const { start, end } = JSON.parse(shopTimings);
          console.log(parsedSlots.service);
          const [slotStart, firstSlotEnd] = parsedSlots.service[0].split('-');
          const [_, slotEnd] = parsedSlots.service[parsedSlots.service.length - 1].split('-');

          if (start !== slotStart && end !== slotEnd) {
            const [e] = firstSlotEnd.split(':');
            const [s] = slotStart.split(':');
            const diff = e - s;
            console.log(diff);

            setCustomSlotTimings({ service: { start: slotStart, end: slotEnd } });
            setUseShopTimings({ service: false });
            setFormData((p) => ({ ...p, serviceSlotInterval: String(diff) }));
          }
        }

        // Check if this is an Event vendor
        const isEventVendorValue = findAttrVal(ATTRIBUTE_KEYS.IS_EVENT_VENDOR);
        setIsEventVendor(isEventVendorValue === 'true');
      } catch (error) {
        console.error('Failed to fetch master attributes:', error);
        toast.error('Could not load attribute settings.');
        setFormData((prev) => ({ ...prev, attributes: vendor.attributeValues || [] }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndMergeAttributes();
  }, [vendor, parseCertificationsString, parseSemicolonList]);

  useEffect(() => {
    !isLoading && setAttributeValue('certifications', stringifyCertificatesArray(certificates));
  }, [certificates, isLoading, setAttributeValue, stringifyCertificatesArray]);

  useEffect(() => {
    !isLoading && setAttributeValue('franchiseName', stringifySemicolonList(franchises));
  }, [franchises, isLoading, setAttributeValue, stringifySemicolonList]);

  const generateTimeSlots = (
    slotInterval,
    businessType = null,
    endTime = null,
    useShop = false
  ) => {
    let slotStart, slotEnd;
    console.log(businessType);
    console.log(useShopTimings);

    if (useShopTimings[businessType] || useShop) {
      slotStart = getNestedAttributeValue('shopTiming', 'start');
      slotEnd = getNestedAttributeValue('shopTiming', 'end');
    } else {
      slotStart = customSlotTimings[businessType]?.start;
      slotEnd = customSlotTimings[businessType]?.end || endTime;
    }

    console.log(slotStart);
    console.log(slotEnd);

    if (!slotStart || !slotEnd || !slotInterval) return [];
    const slots = [];
    const intervalMinutes = Number.parseInt(slotInterval) * 60;
    const [startHour, startMinute] = slotStart.split(':').map(Number);
    const [endHour, endMinute] = slotEnd.split(':').map(Number);
    let currentTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    while (currentTotalMinutes < endTotalMinutes) {
      const slotStartHour = Math.floor(currentTotalMinutes / 60);
      const slotStartMinute = currentTotalMinutes % 60;
      let nextTotalMinutes = currentTotalMinutes + intervalMinutes;
      if (nextTotalMinutes > endTotalMinutes) nextTotalMinutes = endTotalMinutes;
      const slotEndHour = Math.floor(nextTotalMinutes / 60);
      const slotEndMinute = nextTotalMinutes % 60;
      const formattedStart = `${slotStartHour.toString().padStart(2, '0')}:${slotStartMinute.toString().padStart(2, '0')}`;
      const formattedEnd = `${slotEndHour.toString().padStart(2, '0')}:${slotEndMinute.toString().padStart(2, '0')}`;
      slots.push(`${formattedStart}-${formattedEnd}`);
      currentTotalMinutes = nextTotalMinutes;
    }
    return slots;
  };

  const initializeSlots = (endTime = null, interval = 1, useShop = false) => {
    const currentSlots = getDeliverySlotsValue();
    console.log(currentSlots);

    const newSlots = generateTimeSlots(interval, 'service', endTime, useShop);
    console.log(newSlots);

    const slotsChanged = JSON.stringify(currentSlots.service) !== JSON.stringify(newSlots);
    console.log(slotsChanged);

    if (slotsChanged) {
      setDeliverySlotsValue('service', newSlots);
    }
  };

  useEffect(() => {
    if (user) {
      setVendor(user);
    }
  }, [user]);

  const handleSubmit = async () => {
    try {
      const weeklyOffDayString = selectedDays.join(';');
      const finalAttributes = formData.attributes.map((attr) => {
        if (attr.name === ATTRIBUTE_KEYS.WEEKLY_OFF_DAY) {
          return { ...attr, value: weeklyOffDayString };
        }
        return attr;
      });
      setIsSubmitting(true);
      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/attribute`,
        finalAttributes,
        {
          headers: {
            'X-Vendor-Id': vendor.id,
            sessionToken: localStorage.getItem('sessionToken'),
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        }
      );
      toast.success('Vendor attributes updated successfully!');
      const contactNo = localStorage.getItem('contactNo');
      refreshUser(contactNo, true);
      navigate(-1);
    } catch (error) {
      console.error('Error updating vendor attributes:', error);
      toast.error(error.response?.data?.message || 'Failed to update attributes.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !vendor)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <BiglocalLoader title="Fetching your Business Details" />
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <style>{`
        .ql-container {
          font-size: 14px;
          font-family: inherit;
        }
        .ql-editor {
          min-height: 200px;
          max-height: 400px;
          overflow-y: auto;
        }
        .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
        }
        .ql-toolbar {
          background-color: #f8fafc;
          border-color: #e2e8f0 !important;
        }
        .ql-container {
          border-color: #e2e8f0 !important;
        }
        .ql-snow .ql-stroke {
          stroke: #475569;
        }
        .ql-snow .ql-fill {
          fill: #475569;
        }
        .ql-snow .ql-picker-label {
          color: #475569;
        }
      `}</style>
      <div className="max-w-8xl mx-auto px-2 pt-4 sm:px-2 lg:px-4">
        <header className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <ArrowLeft onClick={() => navigate(-1)} className="size-6 cursor-pointer" />

            <div>
              <h1 className="text-base md:text-2xl font-bold text-slate-900">
                {isEventVendor ? 'Event Configuration' : 'Shop Settings'}
              </h1>
              <p className="text-xs md:text-sm text-slate-500">
                {isEventVendor
                  ? 'Configure your event details and information'
                  : 'Configure your business operations and service settings'}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-green-600 text-sm gap-1 px-2 py-1 hover:bg-green-700"
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </header>
        {isEventVendor ? (
          // Event Vendor View
          <div className="space-y-4">
            {/* Event Date and Venue - Side by side on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" /> Event Date
                  </CardTitle>
                  <CardDescription>Specify when the event will take place</CardDescription>
                </CardHeader>
                <CardContent>
                  <Input
                    type="date"
                    value={getAttributeValue(ATTRIBUTE_KEYS.EVENT_DATE)}
                    onChange={(e) => setAttributeValue(ATTRIBUTE_KEYS.EVENT_DATE, e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Select the date when your event will be held
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Home className="w-5 h-5 mr-2" /> Event Venue
                  </CardTitle>
                  <CardDescription>Enter the complete venue address for the event</CardDescription>
                </CardHeader>
                <CardContent>
                  <textarea
                    value={getAttributeValue(ATTRIBUTE_KEYS.EVENT_VENUE)}
                    onChange={(e) => setAttributeValue(ATTRIBUTE_KEYS.EVENT_VENUE, e.target.value)}
                    placeholder="Enter event venue address..."
                    rows={1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Provide the complete address where the event will be held
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <FileText className="w-5 h-5 mr-2" /> Event Details
                    </CardTitle>
                    <CardDescription>
                      Describe your event, schedule, speakers, and other important information
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAttributeValue(ATTRIBUTE_KEYS.EVENT_DETAILS, '')}
                    className="flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <ReactQuill
                    theme="snow"
                    value={getAttributeValue(ATTRIBUTE_KEYS.EVENT_DETAILS)}
                    onChange={(content) => setAttributeValue(ATTRIBUTE_KEYS.EVENT_DETAILS, content)}
                    placeholder="Enter detailed information about your event..."
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Include event schedule, speakers, topics, and any other relevant details. Use the
                  toolbar above to format your text.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <FileText className="w-5 h-5 mr-2" /> Announcement
                    </CardTitle>
                    <CardDescription>
                      Important notices, updates, or last-minute changes for attendees
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAttributeValue(ATTRIBUTE_KEYS.ANNOUNCEMENT, '')}
                    className="flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                  <ReactQuill
                    theme="snow"
                    value={getAttributeValue(ATTRIBUTE_KEYS.ANNOUNCEMENT)}
                    onChange={(content) => setAttributeValue(ATTRIBUTE_KEYS.ANNOUNCEMENT, content)}
                    placeholder="Enter announcements or important notices..."
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        [{ align: [] }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Display important updates or notices for event attendees. Use the toolbar to
                  format your announcements.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Regular Vendor View
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="space-y-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Store className="w-5 h-5 mr-2" /> Business Hours & Operations
                  </CardTitle>
                  <CardDescription>Configure your shop timing and weekly schedule</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                    <TimeSelector
                      label="Opening Time*"
                      value={getNestedAttributeValue('shopTiming', 'start')}
                      onChange={(v) => setNestedAttributeValue('shopTiming', 'start', v)}
                    />
                    <TimeSelector
                      label="Closing Time*"
                      value={getNestedAttributeValue('shopTiming', 'end')}
                      onChange={(v) => setNestedAttributeValue('shopTiming', 'end', v)}
                      startTime="5:00 PM"
                    />
                  </div>

                  <div className="pt-2">
                    <Label className="font-semibold text-slate-800">
                      Lunch break or off-hours?
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Enable if you have break hours during the day
                    </p>
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => setNoOffHours(true)}
                        variant={noOffHours ? 'default' : 'outline'}
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Yes
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setNoOffHours(false);
                          setNestedAttributeValue('offHours', 'start', '');
                          setNestedAttributeValue('offHours', 'end', '');
                        }}
                        variant={!noOffHours ? 'default' : 'outline'}
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        No
                      </Button>
                    </div>
                  </div>

                  {noOffHours && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <TimeSelector
                        label="Break Start*"
                        value={getNestedAttributeValue('offHours', 'start')}
                        onChange={(v) => setNestedAttributeValue('offHours', 'start', v)}
                        startTime="12:00 PM"
                      />
                      <TimeSelector
                        label="Break End*"
                        value={getNestedAttributeValue('offHours', 'end')}
                        onChange={(v) => setNestedAttributeValue('offHours', 'end', v)}
                        startTime="12:00 PM"
                      />
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold text-slate-800">Weekly Off Days</Label>
                    </div>
                    <MultiSelectDropdown
                      options={WEEKLY_OFF_DAYS}
                      selectedValues={selectedDays}
                      onChange={setSelectedDays}
                      placeholder="Select weekly off days..."
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Store className="w-5 h-5 mr-2" /> Delivery & Order Settings
                  </CardTitle>
                  <CardDescription>
                    Configure delivery charges and minimum order requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                    <InputWithIcon
                      id="deliveryCharges"
                      label="Delivery Charges (₹)"
                      helperText="*if less than minimum order"
                      value={getAttributeValue('deliveryCharges')}
                      onChange={(e) => setAttributeValue('deliveryCharges', e.target.value)}
                      type="number"
                      placeholder="e.g., 20"
                      icon={IndianRupee}
                    />
                    <InputWithIcon
                      id="minimumOrder"
                      label="Minimum Order (₹)"
                      helperText="*for free delivery"
                      value={getAttributeValue('minimumOrder')}
                      onChange={(e) => setAttributeValue('minimumOrder', e.target.value)}
                      type="number"
                      placeholder="e.g., 200"
                      icon={IndianRupee}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Store className="w-5 h-5 mr-2" /> Franchise Information
                  </CardTitle>
                  <CardDescription>Specify if you operate under a franchise brand</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="font-semibold text-slate-800">Are you a Franchise?*</Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Select if you operate under a franchise brand
                    </p>
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => setAttributeValue('isFranchise', 'true')}
                        variant={
                          getAttributeValue('isFranchise') === 'true' ? 'default' : 'outline'
                        }
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Yes
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setAttributeValue('isFranchise', 'false');
                          setFranchises([]);
                        }}
                        variant={
                          getAttributeValue('isFranchise') === 'false' ? 'default' : 'outline'
                        }
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        No
                      </Button>
                    </div>
                  </div>

                  {getAttributeValue('isFranchise') === 'true' && (
                    <div className="pt-2">
                      <Label className="font-semibold text-slate-800 mb-2 block">
                        Franchise Brands
                      </Label>
                      {franchises.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 mt-2">
                          <Input
                            value={f.name}
                            onChange={(e) =>
                              setFranchises((p) =>
                                p.map((item, idx) =>
                                  idx === i ? { ...item, name: e.target.value } : item
                                )
                              )
                            }
                            placeholder="e.g., Amul"
                            className="focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            onClick={() => setFranchises((p) => p.filter((_, idx) => idx !== i))}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => setFranchises((p) => [...p, { name: '' }])}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Brand
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pt-4 pb-2">
                  <CardTitle className="flex items-center">
                    <Truck className="w-5 h-5 mr-2" /> Product/Food Delivery Configuration
                  </CardTitle>
                  <CardDescription>Configure how customers receive product orders</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 py-3">
                  <div>
                    <Label className="font-semibold text-slate-800 mb-2 block">
                      I. Products / Food Delivery Type
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">How customers receive orders</p>
                    <DeliveryTypeSelector
                      value={getAttributeValue(ATTRIBUTE_KEYS.PRODUCT_DELIVERY_TYPE)}
                      getAttributeValue={getAttributeValue}
                      setAttributeValue={setAttributeValue}
                      onChange={(value) => {
                        if (getAttributeValue(ATTRIBUTE_KEYS.PRODUCT_DELIVERY_TYPE) === value) {
                          setAttributeValue(ATTRIBUTE_KEYS.PRODUCT_DELIVERY_TYPE, '');
                          setDeliveryPreparationTimeValue('product', '');
                          setAttributeValue(ATTRIBUTE_KEYS.MIN_DAYS, '');
                          setAttributeValue(ATTRIBUTE_KEYS.MAX_DAYS, '');
                        } else {
                          setAttributeValue(ATTRIBUTE_KEYS.PRODUCT_DELIVERY_TYPE, value);
                        }
                      }}
                      options={DELIVERY_TYPE_OPTIONS.product}
                      label=""
                      getDeliveryPreparationTimeValue={getDeliveryPreparationTimeValue}
                      getDeliveryMethodValue={getDeliveryMethodValue}
                      setDeliveryPreparationTimeValue={setDeliveryPreparationTimeValue}
                      DELIVERY_PREPARATION_TIMES={DELIVERY_PREPARATION_TIMES}
                      setDeliveryMethodValue={setDeliveryMethodValue}
                      customSlotTimings={customSlotTimings}
                      handleAttributeChange={handleAttributeChange}
                      validationErrors={validationErrors}
                      setCustomSlotTimings={setCustomSlotTimings}
                      formData={formData}
                      setFormData={setFormData}
                      getDeliverySlotsValue={getDeliverySlotsValue}
                      setValidationErrors={setValidationErrors}
                      type="product"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pt-4 pb-2">
                  <CardTitle className="flex items-center">
                    <Truck className="w-5 h-5 mr-2" /> Service Provider Configuration
                  </CardTitle>
                  <CardDescription>Configure service booking slots for customers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 py-3">
                  <div>
                    <Label className="font-semibold text-slate-800 mb-2 block">
                      II. Are you a Service Provider?
                    </Label>
                    <p className="text-sm text-slate-500 mb-3">
                      e.g., Dry Cleaners, Pathology, etc.
                    </p>
                    <div className="flex gap-4">
                      <Button
                        onClick={() => {
                          setProvidesServices(true);
                          initializeSlots(null, '1', true);
                        }}
                        variant={providesServices ? 'default' : 'outline'}
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Yes
                      </Button>
                      <Button
                        onClick={() => {
                          setProvidesServices(false);
                          setUseShopTimings({ service: true });
                          setCustomSlotTimings({
                            service: { start: '', end: '' },
                          });
                          setFormData((prevFormData) => {
                            const newAttributes = prevFormData.attributes.map((attr) => {
                              if (attr.name === ATTRIBUTE_KEYS.DELIVERY_SLOTS) {
                                const currentSlots = attr.value ? JSON.parse(attr.value) : {};
                                delete currentSlots.service;
                                return { ...attr, value: JSON.stringify(currentSlots) };
                              }
                              return attr;
                            });

                            return {
                              ...prevFormData,
                              serviceSlotInterval: '1',
                              attributes: newAttributes,
                            };
                          });
                        }}
                        variant={!providesServices ? 'default' : 'outline'}
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        No
                      </Button>
                    </div>
                  </div>

                  {providesServices && (
                    <div className="space-y-4 pt-3 border-t">
                      <h4 className="font-semibold text-slate-800">Pickup Slots Configuration</h4>

                      <div>
                        <Label className="font-semibold text-slate-800 mb-2 block">
                          Slot Time Range
                        </Label>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            type="button"
                            onClick={() => {
                              setUseShopTimings((prev) => ({ ...prev, service: true }));
                              setCustomSlotTimings({
                                service: { start: '', end: '' },
                              });
                              initializeSlots(null, formData.serviceSlotInterval, true);
                            }}
                            variant={useShopTimings?.service ? 'default' : 'outline'}
                            className="focus-visible:ring-offset-0 focus-visible:ring-1"
                          >
                            Use Business Hours
                          </Button>
                          <Button
                            type="button"
                            onClick={() => {
                              setUseShopTimings((prev) => ({ ...prev, service: false }));
                              setFormData((prevFormData) => {
                                const newAttributes = prevFormData.attributes.map((attr) => {
                                  if (attr.name === ATTRIBUTE_KEYS.DELIVERY_SLOTS) {
                                    const currentSlots = attr.value ? JSON.parse(attr.value) : {};
                                    delete currentSlots.service;
                                    return { ...attr, value: JSON.stringify(currentSlots) };
                                  }
                                  return attr;
                                });

                                return {
                                  ...prevFormData,
                                  serviceSlotInterval: '1',
                                  attributes: newAttributes,
                                };
                              });
                            }}
                            variant={!useShopTimings?.service ? 'default' : 'outline'}
                            className="focus-visible:ring-offset-0 focus-visible:ring-1"
                          >
                            Set Custom Hours
                          </Button>
                        </div>
                      </div>

                      {!useShopTimings?.service && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="font-semibold text-slate-800 mb-2 block">
                              Slot Start Time*
                            </Label>
                            <TimeSelector
                              value={customSlotTimings.service.start}
                              onChange={(v) => {
                                setCustomSlotTimings((p) => ({
                                  ...p,
                                  service: { ...p.service, start: v },
                                }));
                              }}
                              startTime="8:00 AM"
                            />
                            {!customSlotTimings?.service?.start && (
                              <p className="text-xs text-red-600 mt-1">
                                Please select the start time
                              </p>
                            )}
                          </div>
                          <div>
                            <Label className="font-semibold text-slate-800 mb-2 block">
                              Slot End Time*
                            </Label>
                            <TimeSelector
                              value={customSlotTimings?.service?.end}
                              onChange={(v) => {
                                setCustomSlotTimings((p) => ({
                                  ...p,
                                  service: { ...p.service, end: v },
                                }));
                                initializeSlots(v, formData.serviceSlotInterval);
                              }}
                              startTime="1:00 PM"
                            />
                            {!customSlotTimings?.service?.end && (
                              <p className="text-xs text-red-600 mt-1">
                                Please select the end time
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      <div>
                        <Label className="font-semibold text-slate-800 mb-2 block">
                          Time Slot Interval*
                        </Label>
                        <p className="text-xs text-slate-500 mb-2">
                          The duration of each bookable appointment slot
                        </p>
                        <Select
                          value={formData.serviceSlotInterval}
                          onValueChange={(v) => {
                            setFormData((p) => ({ ...p, serviceSlotInterval: v }));
                            initializeSlots(null, v, useShopTimings?.service);
                          }}
                        >
                          <SelectTrigger className="focus:ring-1 focus:ring-green-500 focus:ring-offset-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DELIVERY_SLOT_INTERVALS.map((i) => (
                              <SelectItem key={i.value} value={i.value}>
                                {i.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {useShopTimings?.service &&
                          getDeliverySlotsValue()?.service?.length < 1 && (
                            <p className="text-xs text-red-600 mt-2">
                              You must set Business Hours before using this option
                            </p>
                          )}
                      </div>

                      {getDeliverySlotsValue()?.service?.length > 0 && (
                        <div>
                          <Label className="font-semibold text-slate-800 mb-3 block">
                            Slot Summary
                          </Label>
                          <div className="max-h-48 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                              {getDeliverySlotsValue()?.service?.map((slot, index) => (
                                <div
                                  key={index}
                                  className="bg-green-100 text-green-800 px-3 py-2 rounded-md text-sm text-center font-medium"
                                >
                                  {slot}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            <div className="space-y-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center">
                    <Wallet2 className="w-5 h-5 mr-2" /> Payment Methods
                  </CardTitle>
                  <CardDescription>Select accepted payment methods for orders</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="font-semibold text-slate-800 mb-2 block">
                      Payment Options*
                    </Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Choose how customers can pay for orders
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        onClick={() =>
                          setAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD, 'Pay On Delivery')
                        }
                        variant={
                          getAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD) === 'Pay On Delivery'
                            ? 'default'
                            : 'outline'
                        }
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Pay On Delivery
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD, 'Prepaid')}
                        variant={
                          getAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD) === 'Prepaid'
                            ? 'default'
                            : 'outline'
                        }
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Prepaid
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD, 'Both')}
                        variant={
                          getAttributeValue(ATTRIBUTE_KEYS.PAYMENT_METHOD) === 'Both'
                            ? 'default'
                            : 'outline'
                        }
                        className="focus-visible:ring-offset-0 focus-visible:ring-1"
                      >
                        Both
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="w-5 h-5 mr-2" /> Legal & Business Details
                  </CardTitle>
                  <CardDescription>
                    Provide legal identification and certification details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="font-semibold text-slate-800 mb-2 block">
                      Business Information
                    </Label>
                    <div className="space-y-3">
                      <InputWithIcon
                        id="establishedYear"
                        label={getAttributeLabel(ATTRIBUTE_KEYS.ESTABLISHED_YEAR)}
                        value={getAttributeValue(ATTRIBUTE_KEYS.ESTABLISHED_YEAR)}
                        onChange={(e) =>
                          handleAttributeChange(ATTRIBUTE_KEYS.ESTABLISHED_YEAR, e.target.value)
                        }
                        type="number"
                        placeholder="e.g., 2020"
                        max={new Date().getFullYear()}
                        icon={Calendar}
                        error={validationErrors.establishedYear}
                      />
                      <InputWithIcon
                        id="panNo"
                        label={getAttributeLabel(ATTRIBUTE_KEYS.PAN_NO)}
                        value={getAttributeValue(ATTRIBUTE_KEYS.PAN_NO)}
                        onChange={(e) =>
                          handleAttributeChange(ATTRIBUTE_KEYS.PAN_NO, e.target.value.toUpperCase())
                        }
                        placeholder="e.g., ABCDE1234F"
                        icon={FileText}
                        error={validationErrors.panNo}
                      />
                      <InputWithIcon
                        id="gstNo"
                        label={getAttributeLabel(ATTRIBUTE_KEYS.GST_NO)}
                        value={getAttributeValue(ATTRIBUTE_KEYS.GST_NO)}
                        onChange={(e) =>
                          handleAttributeChange(ATTRIBUTE_KEYS.GST_NO, e.target.value.toUpperCase())
                        }
                        placeholder="e.g., 22AAAAA0000A1Z5"
                        icon={FileText}
                        error={validationErrors.gstNo}
                      />
                      <InputWithIcon
                        id="udyamNo"
                        label={getAttributeLabel(ATTRIBUTE_KEYS.UDYAM_NO)}
                        value={getAttributeValue(ATTRIBUTE_KEYS.UDYAM_NO)}
                        onChange={(e) =>
                          handleAttributeChange(
                            ATTRIBUTE_KEYS.UDYAM_NO,
                            e.target.value.toUpperCase()
                          )
                        }
                        placeholder="e.g., UDYAM-XX-00-0000000"
                        icon={FileText}
                        error={validationErrors.udyamNo}
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="font-semibold text-slate-800">Certifications</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCertificates((p) => [...p, { name: '', number: '' }])}
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Certificate
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {certificates.map((cert, index) => (
                        <div key={index} className="p-3 border rounded-lg bg-slate-50 space-y-3">
                          <div>
                            <Label className="text-xs font-medium text-slate-700 mb-1 block">
                              Certificate Name
                            </Label>
                            <Input
                              type="text"
                              value={cert.name}
                              onChange={(e) =>
                                setCertificates((p) =>
                                  p.map((c, i) =>
                                    i === index ? { ...c, name: e.target.value } : c
                                  )
                                )
                              }
                              placeholder="e.g., FSSAI"
                              className="focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-slate-700 mb-1 block">
                              Certificate Number
                            </Label>
                            <Input
                              type="text"
                              value={cert.number}
                              onChange={(e) =>
                                setCertificates((p) =>
                                  p.map((c, i) =>
                                    i === index ? { ...c, number: e.target.value } : c
                                  )
                                )
                              }
                              placeholder="e.g., CERT12345"
                              className="focus-visible:ring-green-500 focus-visible:ring-offset-0 focus-visible:ring-1"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="w-full"
                            onClick={() => setCertificates((p) => p.filter((_, i) => i !== index))}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Certificate
                          </Button>
                        </div>
                      ))}
                      {certificates.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-6 bg-slate-50 rounded-lg border border-dashed">
                          No certificates added yet. Click "Add Certificate" to get started.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        <footer className="flex items-center justify-end pt-3">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-green-600 text-sm gap-1 px-2 py-1 hover:bg-green-700"
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default VendorShopSettings;
